import api from '~/services/api';
import { ParamentrosValidacao, ProductDataToTable } from '../../protocols';

type Status = {
  codStatus: number;
  messageStatus: string;
  origem: string;
  restricao: 'BLOQ' | 'INF';
};

/**
 * Adiciona um status ao item apenas se ainda não existir (comparação por chave estável).
 */
const pushStatusUnique = (item: ProductDataToTable, status: Status): void => {
  item.statusDaValidacao = item.statusDaValidacao || [];
  const key = (s: Status) =>
    `${s.codStatus}|${s.origem}|${s.restricao}|${s.messageStatus}`;

  const exists = item.statusDaValidacao.some(
    (s) => key(s as Status) === key(status),
  );
  if (!exists) item.statusDaValidacao.push(status);
};

/**
 * Remove duplicatas pré-existentes (caso o método já tenha sido chamado antes).
 */
const dedupeStatuses = (item: ProductDataToTable): void => {
  const list = item.statusDaValidacao || [];
  const seen = new Set<string>();
  const buildKey = (s: Status) =>
    `${s.codStatus}|${s.origem}|${s.restricao}|${s.messageStatus}`;
  item.statusDaValidacao = list.filter((s) => {
    const k = buildKey(s as Status);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

/**
 * Valida IBS/CBS para um item de produto.
 * - Idempotente: não duplica mensagens em chamadas repetidas.
 * - Mantém mensagens 9000/9001/9002 únicas.
 */
export const validarIBSCBS = async (
  item: ProductDataToTable,
  parametros: ParamentrosValidacao,
): Promise<ProductDataToTable> => {
  try {
    // sanity + de-dup inicial (caso já tenha sido validado antes)
    dedupeStatuses(item);

    if (
      !parametros?.flg_considera_ibs_cbs ||
      !item?.des_classif_trib_xml ||
      item.des_classif_trib_xml.trim() === ''
    ) {
      return item;
    }

    const descricao = item.des_classif_trib_xml.trim();

    // 1) Busca o código IBS/CBS no ERP por descrição
    const { data } = await api.post('/nf/ibs-cbs/classificacao', {
      des_classif_trib: descricao,
    });

    const found =
      data?.success && Array.isArray(data?.data) && data.data.length > 0
        ? data.data[0]
        : null;

    if (!found) {
      // Classificação NÃO cadastrada
      pushStatusUnique(item, {
        codStatus: 9001,
        messageStatus: `Classif.Tributária IBS/CBS não cadastrada: ${descricao}`,
        origem: 'TRIB',
        restricao: 'BLOQ',
      });
      item.cod_ibs_cbs = null;
      return item;
    }

    // Classificação ENCONTRADA
    item.cod_ibs_cbs = Number(found.cod_ibs_cbs);

    // 2) Verifica divergência entre XML e cadastro do produto (informativo)
    const { data: dataClass } = await api.get(
      `/nf/ibs-cbs/classificacao/${item.cod_produto}`,
    );
    const classifDoProduto = dataClass?.data;

    if (
      typeof classifDoProduto === 'string' &&
      classifDoProduto.trim() !== '' &&
      classifDoProduto.trim() !== descricao
    ) {
      pushStatusUnique(item, {
        codStatus: 9002,
        messageStatus:
          'Classif.Tributária IBS/CBS do xml diverge da informada no cadastro do produto',
        origem: 'TRIB',
        restricao: 'INF',
      });
    }

    return item;
  } catch {
    pushStatusUnique(item, {
      codStatus: 9000,
      messageStatus:
        'Falha ao validar IBS/CBS. Tente novamente ou contate o suporte.',
      origem: 'TRIB',
      restricao: 'INF',
    });
    return item;
  }
};
